import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  InputCheckBox,
  SelectOptionGroups,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  CreatePostDocument,
  GroupsWithPostPermissionDocument,
  PostDataFragment,
  SearchEmployeesForMentionsDocument,
  UpdatePostDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import TextEditor from 'modules/core/components/TextEditor'
import { MentionedAccess } from 'modules/core/components/TextEditor/plugins/DebouncedMentionsPlugin'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, FormProvider, useFormContext } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCommunitiesClapsFeature } from 'modules/posts/hooks/useCommunitiesClapsFeature'

import Attachments from './Attachments'
import Event from './Event'
import ImageUploader from './ImageUploader'
import PublishModal from './PublishModal'
import { PostFormValues } from './types'

interface InnerProps extends Props {
  openModal: boolean
  setOpenModal: (value: boolean) => void
  setNewAttachments: (attachments: File[]) => void
  attachmentIdsToDelete: number[]
  setAttachmentIdsToDelete: (ids: number[]) => void
  groupOptions: SelectOptionGroups
  isEvent?: boolean
}

type TextEditor = React.ComponentProps<typeof TextEditor>

const ManagePost = ({
  isKudos,
  post,
  openModal,
  setOpenModal,
  setNewAttachments,
  attachmentIdsToDelete,
  setAttachmentIdsToDelete,
  groupOptions,
  isEvent,
}: InnerProps) => {
  const form = useFormContext<PostFormValues>()
  const [openTooltip, toggleTooltip] = useToggle(false)
  const hasMentioning = useCommunitiesClapsFeature()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  React.useEffect(() => {
    if (
      !post &&
      Array.isArray(groupOptions) &&
      groupOptions.length > 0 &&
      form.values.postGroupId === 0
    ) {
      const firstId = groupOptions[0]?.value
      if (firstId) {
        form.setValues({ ...form.values, postGroupId: firstId })
      }
    }
  }, [groupOptions, post, form])

  const [searchEmployees] = useLazyQuery(SearchEmployeesForMentionsDocument)

  const handleMentionSearch = React.useCallback(
    async (queryString: string): Promise<MentionedAccess[]> => {
      if (!queryString) {
        return []
      }

      try {
        const result = await searchEmployees({
          variables: { companyId, search: queryString },
        })

        const nodes = result.data?.employees?.employeeNamesConnection?.nodes
        if (!nodes) return []

        return nodes.map((employee) => ({
          id: employee.access.id,
          employeeId: employee.id,
          fullName: employee.fullName ?? '',
          avatar: employee.avatar?.url ?? '',
        }))
      } catch {
        return []
      }
    },
    [companyId, searchEmployees]
  )

  const onTextEditorChange: TextEditor['onChange'] = (eventData) => {
    if (eventData) {
      if (typeof eventData === 'object') {
        form.setValues({
          ...form.values,
          description: eventData.value,
          mentionedAccessIds: eventData.ids,
        })
      } else {
        form.setValues({
          ...form.values,
          description: eventData,
        })
      }
    }
  }

  useEffect(() => {
    form.validateForm()
    // NOTE: validating the form change its state so adding form as
    //       dependency will cause an infinite loop here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClick = () => {
    setOpenModal(true)
    track(trackingEvents.communities.createPostContinue)
  }

  return (
    <Box paddingX="s200" paddingY="s16" width="full" gap="s24" alignItems="center">
      <Box gap="s8">
        <ImageUploader />
        <Box width="s600" alignItems="flexStart">
          <Text size="200" color="radicalRed1000" capitalize>
            {form.errors.coverImageUrl}
          </Text>
        </Box>
      </Box>
      <Box width="s600" aria-label={i18n.t('posts.communities.aria_label_posts_title_field')}>
        <Text.Normal size="200" color="grey1000">
          {i18n.t('posts.communities.title')}
        </Text.Normal>
        <TextField
          value={form.values.title}
          placeholder={i18n.t('posts.communities.title_placeholder')}
          onChange={(title) => form.setValues({ ...form.values, title })}
          mandatory
          error={form.values.title !== form.initialValues.title ? form.errors.title : ''}
        />
      </Box>
      {!post && !isKudos && !isEvent && (
        <Box
          width="s600"
          component="label"
          flexDirection="row"
          flexGrow
          gap="s8"
          alignItems="center"
          justifyContent="flexStart"
          aria-label={i18n.t('posts.communities.aria_label_is_post_an_event_check')}
        >
          <InputCheckBox
            checked={form.values.isEvent}
            onClick={() => form.setValues({ ...form.values, isEvent: !form.values.isEvent })}
          />
          <Text.Normal>{i18n.t('posts.communities.event_check')}</Text.Normal>
        </Box>
      )}
      {form.values.isEvent && <Event isEvent={form.values.isEvent} />}
      <Box width="s600" aria-label={i18n.t('posts.communities.aria_label_posts_description_field')}>
        <Text.Normal size="200" color="grey1000">
          {i18n.t('posts.communities.description')}
        </Text.Normal>
        {hasMentioning ? (
          <TextEditor
            initialEditorState={{ data: form.values.description }}
            onChange={onTextEditorChange}
            onFiles={(attachments) => setNewAttachments(attachments)}
            placeholder={
              isKudos
                ? i18n.t('posts.kudos.description_placeholder')
                : i18n.t('posts.communities.description_placeholder')
            }
            height="s200"
            hasDebouncedMentions
            onMentionQueryStringChanged={handleMentionSearch}
            enableBetaFeatures={false}
          />
        ) : (
          <TextEditor
            initialEditorState={{ data: form.values.description }}
            onChange={onTextEditorChange}
            onFiles={(attachments) => setNewAttachments(attachments)}
            placeholder={i18n.t('posts.communities.description_placeholder')}
            height="s200"
            enableBetaFeatures={false}
          />
        )}
        <Text size="200" color="radicalRed1000" capitalize>
          {form.values.description !== form.initialValues.description
            ? form.errors.description
            : ''}
        </Text>
        <Box alignItems="flexEnd">
          <Text.Normal size="100" color="grey800">
            {i18n.t('posts.communities.attachments_size_per_post')}
          </Text.Normal>
        </Box>
      </Box>
      {post && post.savedAttachmentsConnection.edges.length > 0 && (
        <Attachments
          post={post}
          attachmentIdsToDelete={attachmentIdsToDelete}
          setAttachmentIdsToDelete={setAttachmentIdsToDelete}
        />
      )}
      {openModal && (
        <PublishModal
          isSubmitting={form.isSubmitting}
          onSubmit={() => {
            form.handleSubmit()
          }}
          onCloseModal={() => {
            setOpenModal(false)
          }}
          groupOptions={groupOptions}
        />
      )}
      <Box justifyContent="flexStart" width="s600">
        <>
          {form.isValid ? (
            <Button
              onClick={() => handleClick()}
              type="primary"
              disabled={!form.isValid || form.isSubmitting}
            >
              {i18n.t('posts.communities.continue')}
            </Button>
          ) : (
            <Tooltip.Alt
              attachment="top center"
              open={openTooltip}
              toggle={toggleTooltip}
              label={i18n.t('posts.communities.continue_button_info')}
            >
              <Button onClick={() => setOpenModal(true)} type="primary" disabled>
                {i18n.t('posts.communities.continue')}
              </Button>
            </Tooltip.Alt>
          )}
        </>
      </Box>
    </Box>
  )
}

interface Props {
  post?: PostDataFragment
  isKudos?: boolean
  isEvent?: boolean
}

const ManagePostForm = ({ post, isKudos, isEvent = false }: Props) => {
  const location = useLocation()
  const [openModal, setOpenModal] = React.useState(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [newAttachments, setNewAttachments] = React.useState<File[]>([])
  const [attachmentIdsToDelete, setAttachmentIdsToDelete] = React.useState<number[]>([])

  const { data: groupsData, fetchMore } = useQuery(GroupsWithPostPermissionDocument, {
    variables: {
      first: 100,
      filter: {
        identifier: {
          notEq: 'celebrations',
        },
      },
    },
  })

  const isFetchingMore = React.useRef(false)

  React.useEffect(() => {
    const connection = groupsData?.posts?.groupsConnection
    const hasNextPage = connection?.pageInfo?.hasNextPage
    const endCursor = connection?.pageInfo?.endCursor

    if (hasNextPage && endCursor && !isFetchingMore.current) {
      isFetchingMore.current = true
      fetchMore({
        variables: {
          after: endCursor,
          first: 100,
          filter: {
            identifier: {
              notEq: 'celebrations',
            },
          },
        },
      }).finally(() => {
        isFetchingMore.current = false
      })
    }
  }, [groupsData, fetchMore])

  const groupOptions: SelectOptionGroups = React.useMemo(() => {
    const edges = groupsData?.posts?.groupsConnection?.edges
    if (!edges) return []

    return edges
      .map((edge) => edge.node)
      .filter((group) => {
        return group?._policies?.addPosts?.can
      })
      .map((group) => ({
        label: group?.title ?? '',
        value: group?.id ?? 0,
      }))
      .filter((g) => g.value > 0)
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [groupsData])

  const firstGroupId = groupOptions[0]?.value

  const defaultValues: PostFormValues = React.useMemo(
    () => ({
      title: isKudos
        ? i18n.t('posts.kudos.automatic_title_with_emoji', { emojis: '\u{1F44F}' })
        : '',
      description: '',
      isEvent,
      postGroupId: firstGroupId ?? 0,
      allowCommentsAndReactions: true,
      sendNotifications: false,
      startsAt: new Date().toISOString(),
      location: '',
      coverImageUrl: null,
      mentionedAccessIds: [],
    }),
    [isKudos, isEvent, firstGroupId]
  )

  const [createPost] = useMutation(CreatePostDocument, {
    extractorFn: (data) => data.postsMutations.createPostsPost,
  })

  const [updatePost] = useMutation(UpdatePostDocument, {
    extractorFn: (data) => data.postsMutations.updatePostsPost,
  })

  const entity = {
    ...(post ?? {}),
    isEvent: post?.type === 'Posts::V2::Event' || isEvent,
  }

  return (
    <FormProvider
      entity={entity}
      defaultValues={defaultValues}
      validate={(data) => {
        const errors: FormErrors<PostFormValues> = {}

        if (data.title === '') {
          errors.title = i18n.t('common.validations.empty')
        } else if (/<\/?[a-z][\s\S]*>/i.test(data.title)) {
          errors.title = i18n.t('posts.communities.error_input')
        }

        if (data.description === '') {
          errors.description = i18n.t('common.validations.empty')
        }

        if (Object.keys(errors).length > 0) {
          setOpenModal(false)
        }

        return errors
      }}
      onSubmit={async (data, { setErrors }) => {
        if (!data.postGroupId || data.postGroupId === 0) {
          setErrors({
            postGroupId: i18n.t('common.validations.empty'),
          })
          return { success: false, errors: [] }
        }

        const type = isKudos ? 'kudo' : data.isEvent ? 'event' : 'announcement'
        const communityType = type === 'announcement' ? 'default' : 'custom'
        let handled

        if (post) {
          const result = await updatePost({
            variables: {
              id: post.id,
              description: data.description,
              mentionedAccessIds: data.mentionedAccessIds,
              postGroupId: data.postGroupId,
              title: data.title,
              allowCommentsAndReactions: data.allowCommentsAndReactions,
              sendNotifications: data.sendNotifications,
              location: data.location,
              startsAt: data.startsAt,
              deleteCoverImage: data.coverImageUrl === null,
              newCoverImage: data.coverImageUrl instanceof File ? data.coverImageUrl : undefined,
              newAttachments,
              deletedAttachments: attachmentIdsToDelete,
            },
          })

          handled = handleResult(result, (errors) => {
            if (errors.newCoverImage) {
              setErrors({
                coverImageUrl: errors.newCoverImage,
              })
            }
            if (errors.newAttachments) {
              setErrors({
                description: errors.newAttachments,
              })
            }
            app.setFlash({
              type: 'wrong',
              message: i18n.t('posts.communities.error_message'),
            })
          })

          if (isKudos) {
            track(trackingEvents.communities.sendClapsEdit, {
              is_succesfull: handled.success,
            })
          } else {
            track(trackingEvents.communities.createPostEdit, {
              community: communityType,
              allow_comm_react: data.allowCommentsAndReactions,
              send_notifications: data.sendNotifications,
            })
          }
        } else {
          const result = await createPost({
            variables: {
              description: data.description,
              mentionedAccessIds: data.mentionedAccessIds,
              postGroupId: data.postGroupId,
              publishedAt: moment().toISOString(),
              title: data.title,
              type,
              allowCommentsAndReactions: data.allowCommentsAndReactions,
              sendNotifications: data.sendNotifications,
              location: data.location,
              startsAt: data.startsAt,
              coverImage: data.coverImageUrl,
              attachments: newAttachments,
            },
          })

          handled = handleResult(result, (errors) => {
            if (errors.coverImage) {
              setErrors({
                coverImageUrl: errors.coverImage,
              })
            }
            if (errors.attachments || errors.description) {
              setErrors({
                description: errors.attachments || errors.description,
              })
            }
            app.setFlash({
              type: 'wrong',
              message: i18n.t('posts.communities.error_message'),
            })
          })

          if (isKudos) {
            track(trackingEvents.communities.sendClapsSubmit, {
              is_succesfull: handled.success,
            })
          } else {
            track(trackingEvents.communities.createPostSubmit, {
              community: communityType,
              allow_comm_react: data.allowCommentsAndReactions,
              send_notifications: data.sendNotifications,
            })
          }
        }

        if (handled.success && handled.data.post) {
          const isFromCommunities = location.pathname.includes('/communities/post/')
          if (isFromCommunities) {
            navigate(navigation.communities.post.id.fullPath({ id: handled.data.post.id }))
          } else {
            navigate(navigation.dashboard.post.id.fullPath({ id: handled.data.post.id }))
          }
        } else {
          setOpenModal(false)
        }

        return handled
      }}
    >
      <ManagePost
        isKudos={isKudos}
        isEvent={isEvent}
        post={post}
        openModal={openModal}
        setOpenModal={setOpenModal}
        setNewAttachments={setNewAttachments}
        attachmentIdsToDelete={attachmentIdsToDelete}
        setAttachmentIdsToDelete={setAttachmentIdsToDelete}
        groupOptions={groupOptions}
      />
    </FormProvider>
  )
}

export default ManagePostForm
